import React, { useCallback, useEffect, useState, memo } from "react"
import QRCode from "react-qr-code"
import { Box } from "@mui/material"
import { Typography, Button, Stepper, StepLabel, Step, StepContent, TextField, makeStyles } from "@material-ui/core"
import PropTypes from "prop-types"
import { generateTotpUri } from "../../../../store/actions/auth"
import { addKeyToClipboard } from "../../../../utils/generalUtility"

const useStyles = makeStyles((theme) => ({
	paper: {
		overflowY: "scroll",
		overflowX: "hidden",
		height: "100vh",
		padding: theme.spacing(2, 3, 3),
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
	},
	form: {
		width: "100%", // Fix IE 11 issue.
		marginTop: theme.spacing(1),
	},
	submit: {
		marginTop: theme.spacing(3),
	},
	helperText: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
		textAlign: "center",
		fontSize: "0.7rem",
		color: "#333",
	},
}))

const initialInputError = { totp: { error: false, errorText: "" } }
const initialInputTouched = { totp: false }
const softwareMfaSteps = [
	{
		label: "Scan the QR Code",
		description: `Scan the QR code with your devices camera or with your authenticator app to create a token.`,
	},
	{
		label: "Add the code",
		description:
			"Once your account is set up within your authenticator app, add the first time code to complete the setup.",
	},
]

const SetupMFA = memo(({ cognitoUser, loginEntries, verifyTotpToken, enqueueSnackbar, closeSnackbar }) => {
	const classes = useStyles()
	const [softwareTokenUriOktaVerify, setSoftwareTokenUriOktaVerify] = useState("")
	const [softwareTokenUriOtpAuth, setSoftwareTokenUriOtpAuth] = useState("")
	const [totpCode, setTotpCode] = useState("")
	const [inputError, setInputError] = useState({ ...initialInputError })
	const [inputTouched, setInputTouched] = useState({ ...initialInputTouched })
	const [activeStep, setActiveStep] = useState(0)
	const [mfaSetupSecret, setMfaSetupSecret] = useState("")

	const copyKeyHandler = useCallback(() => {
		addKeyToClipboard(mfaSetupSecret)
	}, [mfaSetupSecret])

	useEffect(() => {
		generateTotpUri(cognitoUser)
			.then((response) => {
				const { secret, uri } = response
				// OtpAuth is used for QR codes and the non iOS deeplink
				setSoftwareTokenUriOtpAuth("otpauth" + uri)
				// "otpauth://" deeplink does not work on iPone devices as it opens the password manager. Instead we use oktaverify://
				// "otpauth://" does work on iPhones if scanned from apps like Okta Verify and Google Authenticator
				// "otpauth://" deeplink and uri work on Android
				// Miguel asked we just have links for Okta as all GameChange users have the app
				setSoftwareTokenUriOktaVerify("oktaverify" + uri)
				// If a user is unable to scan the QR code or use the deep links then they can copy the MFA setup key and paste
				// it into the authentication app of their choosing.
				setMfaSetupSecret(secret)
			})
			.catch((err) => {
				enqueueSnackbar(`Error Generating QR-Code: ${err.message} `, {
					variant: "error",
					persist: true,
					action: (key) => (
						<Button
							onClick={() => {
								closeSnackbar(key)
							}}
						>
							Dismiss
						</Button>
					),
				})
			})
		// disabling because we do not want to add enqueueSnackbar or closeSnackbar to the dependency array
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cognitoUser])

	// TOTP code validation
	useEffect(() => {
		if (totpCode.length === 6 && !isNaN(Number(totpCode))) {
			setInputError({ ...initialInputError })
		} else {
			setInputError({
				...initialInputError,
				totp: {
					error: true,
					errorText: "Number must be 6 digits in length.",
				},
			})
		}
	}, [totpCode])

	const submitTotp = useCallback(() => {
		verifyTotpToken(cognitoUser, totpCode, loginEntries.password, "MFA_SETUP")
			.then()
			.catch((err) => {
				enqueueSnackbar(err.message ?? "An unexpected error occurred.", {
					variant: "error",
					persist: false,
					action: (key) => (
						<Button
							onClick={() => {
								closeSnackbar(key)
							}}
						>
							Dismiss
						</Button>
					),
				})
			})
	}, [cognitoUser, totpCode, loginEntries.password, verifyTotpToken, enqueueSnackbar, closeSnackbar])

	return (
		<div className={classes.paper}>
			<Typography component="h1" variant="h5">
				Setup Multi-Factor Authentication
			</Typography>
			<Box>
				<Stepper activeStep={activeStep} orientation={"vertical"}>
					{softwareMfaSteps.map((step, index) => (
						<Step key={step.label}>
							<StepLabel>{step.label}</StepLabel>
							<StepContent>
								<Typography>{step.description}</Typography>
								<Box sx={{ mt: 2 }}>
									{index === 0 ? (
										<>
											<QRCode value={softwareTokenUriOtpAuth} />
											<div>
												<p>
													Using a mobile phone and cannot scan the code above? Tap on one of these links below to setup
													MFA with Okta Verify app:
												</p>
												<a href={softwareTokenUriOktaVerify}>Okta Verify iOS</a>
												<br />
												<a href={softwareTokenUriOtpAuth}>Okta Verify Android</a>
												<p>
													Alternatively, you can add a key to your authentication app of choice to setup your MFA. Tap
													on the button below to copy the necessary key value.
												</p>
												<Button variant="contained" color="secondary" sx={{ mt: 1, mr: 1 }} onClick={copyKeyHandler}>
													Copy Key
												</Button>
											</div>
										</>
									) : (
										<TextField
											label="Code"
											margin="normal"
											variant="outlined"
											value={totpCode}
											onChange={(e) => {
												setInputTouched({ ...inputTouched, totp: true })
												setTotpCode(e.target.value)
											}}
											error={inputTouched.totp && inputError.totp.error}
											helperText={inputError.totp.errorText}
											fullWidth
											required
										/>
									)}
								</Box>
								<Box sx={{ my: 2 }}>
									<div>
										{index === softwareMfaSteps.length - 1 ? (
											<Button
												disabled={inputError.totp.error}
												variant="contained"
												color="primary"
												onClick={submitTotp}
												sx={{ mt: 1, mr: 1 }}
											>
												Submit
											</Button>
										) : (
											<Button
												variant="contained"
												color="primary"
												onClick={() => {
													setActiveStep((prevActiveStep) => prevActiveStep + 1)
												}}
												sx={{ mt: 1, mr: 1 }}
											>
												Continue
											</Button>
										)}
										{index !== 0 && (
											<Button
												color="primary"
												onClick={() => {
													setActiveStep((prevActiveStep) => prevActiveStep - 1)
												}}
												sx={{ mt: 1, mr: 1 }}
											>
												Back
											</Button>
										)}
									</div>
								</Box>
							</StepContent>
						</Step>
					))}
				</Stepper>
			</Box>
		</div>
	)
})

SetupMFA.propTypes = {
	cognitoUser: PropTypes.object,
	loginEntries: PropTypes.object,
	verifyTotpToken: PropTypes.func,
	enqueueSnackbar: PropTypes.func,
	closeSnackbar: PropTypes.any,
}

SetupMFA.displayName = "SetupMFA"

export default SetupMFA
